<template>
  <div>
    <div v-if="!isPremium">
      <UpgradeToPremium />
    </div>
    <div
      v-else-if="virtualDailyUsageReached"
      class="d-flex flex-column align-items-center justify-content-center py-5"
    >
      <feather-icon
        icon="AlertCircleIcon"
        size="64"
        class="text-danger mb-2"
      />
      <h2 class="mb-1">
        Account limit reached
      </h2>
      <p>You have reached your daily limit of running {{ virtualPortfolioRunsLimit }} virtual portfolios</p>
      <b-button
        variant="primary"
        @click="$router.go(-1)"
      >
        Go Back
      </b-button>
    </div>
    <div
      v-else-if="userCreatedMonthlyLimitReached"
      class="d-flex flex-column align-items-center justify-content-center py-5"
    >
      <feather-icon
        icon="AlertCircleIcon"
        size="64"
        class="text-danger mb-2"
      />
      <h2 class="mb-1">
        Account limit reached
      </h2>
      <p>{{ userCreatedLimitMessage }}</p>
      <b-button
        variant="primary"
        @click="$router.go(-1)"
      >
        Go Back
      </b-button>
    </div>
    <div
      v-else-if="apiError"
      class="d-flex flex-column align-items-center justify-content-center py-5"
    >
      <feather-icon
        icon="AlertCircleIcon"
        size="64"
        class="text-danger mb-2"
      />
      <h2 class="mb-1">
        Something went wrong
      </h2>
      <p>{{ apiError }}</p>
      <b-button
        variant="primary"
        @click="$router.go(-1)"
      >
        Go Back
      </b-button>
    </div>
    <!-- Message when no searchFilter is available for virtual portfolio -->
    <div v-else>
      <div
        v-if="portfolioType === 'v' && !searchFilter && !cachedFilter"
        class="d-flex flex-column py-5 my-5 justify-content-center align-items-center"
      >
        <span class="font-weight-bold h4">
          Please make your selection again to view portfolio analysis
        </span>

        <b-button
          class="mt-2"
          variant="primary"
          @click.prevent="$router.push({name: 'full-database'})"
        >
          Go Back To Full Database
        </b-button>
      </div>

      <!-- Message when user portfolio has no products -->
      <div
        v-else-if="portfolioType !== 'u' && portfolioType !== 'v' && portfolioData && products.length < 1"
        class="d-flex flex-column py-5 my-5 justify-content-center align-items-center"
      >
        <span class="font-weight-bold h4">
          There are no products in this portfolio
        </span>

        <p class="mt-2 text-center">
          Go to 'Saved Portfolios' and upload a spreadsheet, or browse 'All Products' to add products to your portfolio.
        </p>

        <div class="mt-2">
          <b-button
            variant="primary"
            class="mr-1"
            @click.prevent="$router.push({name: 'my-portfolios'})"
          >
            Go to Saved Portfolios
          </b-button>
          <b-button
            variant="outline-primary"
            @click.prevent="$router.push({name: 'full-database'})"
          >
            Go to All Products
          </b-button>
        </div>
      </div>

      <template v-else>
        <portfolio-header
          :portfolio-type="portfolioType"
          :portfolio-name="portfolioName"
          :data-updated="dataUpdated"
          :search-filter="searchFilter || cachedFilter"
          @edit-portfolio="editPortfolio"
          @create-portfolio="createPortfolioModal.show = true"
        />

        <b-tabs
          v-if="portfolioType !== 'u' && portfolioType !== 'v'"
          v-model="weightTabIndex"
          pills
          cards
          class="mt-1 mb-2"
        >
          <b-tab>
            <template #title>
              <feather-icon
                icon="LayersIcon"
                size="16"
                class="mr-50"
              />
              <span>Equally Weighted</span>
            </template>
            <div class="d-flex justify-content-between align-items-center mb-1">
              <p class="mb-0 d-flex align-items-center">
                All products contribute equally regardless of their notional value
                <product-weighting-help class="ml-50" />
              </p>
              <b-button
                variant="outline-secondary"
                size="sm"
                class="d-flex align-items-center"
                :disabled="!canGeneratePortfolioPdf"
                @click="generatePortfolioPdf"
              >
                <b-spinner
                  v-if="isGeneratingPdf"
                  small
                  style="width:20px;height:20px;"
                />
                <b-img
                  v-else
                  :src="require('@/assets/images/icons/file-icons/adobe_pdf_icon_modern.png')"
                  class="pdf-icon-small"
                  style="position: relative; top: -2px;"
                />
                <span class="ml-50 font-weight-bold text-body">View as PDF</span>
              </b-button>
            </div>
          </b-tab>
          <b-tab>
            <template #title>
              <feather-icon
                icon="BarChart2Icon"
                size="16"
                class="mr-50"
              />
              <span>Notional Weighted</span>
            </template>
            <div class="d-flex justify-content-between align-items-center mb-1">
              <p class="mb-0 d-flex align-items-center">
                Products are weighted proportionally by their notional investment amount
                <product-weighting-help class="ml-50" />
              </p>
              <b-button
                v-if="isModeAllowed('notional')"
                variant="outline-secondary"
                size="sm"
                class="d-flex align-items-center"
                :disabled="!canGeneratePortfolioPdf"
                @click="generatePortfolioPdf"
              >
                <b-spinner
                  v-if="isGeneratingPdf"
                  small
                  style="width:20px;height:20px;"
                />
                <b-img
                  v-else
                  :src="require('@/assets/images/icons/file-icons/adobe_pdf_icon_modern.png')"
                  class="pdf-icon-small"
                  style="position: relative; top: -2px;"
                />
                <span class="ml-50 font-weight-bold text-body">View as PDF</span>
              </b-button>
            </div>
            <div
              v-if="!chartDataLoading && (!isModeAllowed('notional') || hasMissingNotionalEntries)"
              class="text-center mt-1 mb-2"
            >
              <h4 class="mb-1">
                {{ isModeAllowed('notional') && hasMissingNotionalEntries ? 'Missing notional entries' : 'No notional entries' }}
              </h4>
              <template v-if="isModeAllowed('notional') && hasMissingNotionalEntries">
                <b-alert
                  show
                  variant="warning"
                  class="d-inline-block mb-1 px-3 py-75"
                >
                  This report is calculated using the notional values entered for each product. Where no notional value has been entered, a value of zero is assumed.
                </b-alert>
                <div>
                  <b-alert
                    show
                    variant="danger"
                    class="d-inline-block mb-1 px-3 py-75"
                  >
                    {{ missingNotionalCount }} {{ missingNotionalCount === 1 ? 'product' : 'products' }} missing notional value
                  </b-alert>
                </div>
              </template>
              <b-alert
                v-else
                show
                variant="danger"
                class="d-inline-block mb-1 px-3 py-75"
              >
                There are no notional values entered for this portfolio
              </b-alert>
              <div>
                <b-button
                  :to="{ name: 'portfolio-edit', params: { portfolioId: portfolioType } }"
                  variant="outline-warning"
                  size="sm"
                >
                  <feather-icon icon="Edit2Icon" />
                  Click here to edit portfolio notionals by product
                </b-button>
              </div>
            </div>
          </b-tab>
        </b-tabs>

        <b-alert
          v-if="!chartDataLoading && portfolioData && portfolioData.calculationstatus && portfolioData.calculationstatus.code === 'no_calculable_products'"
          show
          variant="warning"
          class="px-2 py-1"
        >
          {{ portfolioData.calculationstatus.message }}
        </b-alert>

        <portfolio-chart-analysis
          v-if="isModeAllowed(productWeightMode)"
          :key="filterKey"
          ref="chartAnalysis"
          :portfolio-type="portfolioType"
          :portfolio-data="portfolioData"
          :portfolio-counts="portfolioCounts"
          :evolution-data="evolutionData"
          :index-underlying-vol-ratio="indexUnderlyingVolRatio"
          :product-weight-mode="productWeightMode"
          :search-filter="portfolioType === 'v' ? (searchFilter || cachedFilter) : null"
          :portfolio-name="portfolioName"
          :loading="chartDataLoading"
          @show-index-selector="subIndexListFilterShown = true"
          @show-products="showProductsListModal"
          @download-csv="addPortfolioCsvDownloadHit"
        />

        <b-card
          v-if="showPortfolioProductsTable"
          class="mt-2 portfolio-products-section"
        >
          <div class="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center mb-1">
            <div>
              <h4 class="mb-25">
                Products
              </h4>
              <span class="text-muted">
                {{ portfolioProductsData.length }} {{ portfolioProductsData.length === 1 ? 'product' : 'products' }}
              </span>
            </div>

            <b-input-group class="portfolio-products-search mt-1 mt-lg-0">
              <b-input-group-prepend is-text>
                <feather-icon icon="SearchIcon" />
              </b-input-group-prepend>
              <b-form-input
                v-model="portfolioProductsTable.filter"
                placeholder="Search products"
              />
            </b-input-group>
          </div>

          <b-table
            v-if="portfolioProductsData.length > 0"
            :items="portfolioProductsData"
            :fields="portfolioProductsFields"
            :per-page="portfolioProductsTable.perPage"
            :current-page="portfolioProductsTable.currentPage"
            :sort-by.sync="portfolioProductsTable.sortBy"
            :sort-desc.sync="portfolioProductsTable.sortDesc"
            responsive
            hover
            small
            @row-clicked="onPortfolioProductClicked"
            @row-middle-clicked="onPortfolioProductClicked"
          >
            <template #cell(productTypeName)="data">
              <div class="d-flex align-items-center">
                <b-avatar
                  class="mr-1"
                  :variant="data.item.ptIconColor || 'light-secondary'"
                >
                  <feather-icon
                    size="18"
                    :icon="data.item.ptIconName || 'GridIcon'"
                  />
                </b-avatar>
                <span>{{ data.item.productTypeName || '-' }}</span>
              </div>
            </template>

            <template #cell(productName)="data">
              <div class="d-flex align-items-center">
                <b-img
                  v-if="data.item.providerId"
                  :src="`${baseURL}serveproviderlogo.cfm?id=${data.item.providerId}`"
                  alt="provider logo"
                  class="mr-1 portfolio-product-logo"
                />
                <span>{{ data.item.productName }}</span>
              </div>
            </template>

            <template #cell(status)="data">
              <b-badge :variant="statusVariant(data.item.status)">
                {{ data.item.status || '-' }}
              </b-badge>
            </template>

            <template #cell(notional)="data">
              <span>{{ formatNotional(data.item.notional) }}</span>
            </template>

            <template #cell(exclusionReason)="data">
              <span :class="{ 'text-muted': !hasExclusionReason(data.item) }">
                {{ data.item.exclusionReason || 'None' }}
              </span>
            </template>

            <template #cell(strikeDate)="data">
              <span>{{ data.item.strikeDate ? formatIsoDate(data.item.strikeDate) : '-' }}</span>
            </template>

            <template #cell(finalMarketDate)="data">
              <span>{{ data.item.finalMarketDate ? formatIsoDate(data.item.finalMarketDate) : '-' }}</span>
            </template>

            <template #cell(terminationDate)="data">
              <span>{{ data.item.terminationDate ? formatIsoDate(data.item.terminationDate) : '' }}</span>
            </template>
          </b-table>

          <b-alert
            v-else
            show
            variant="secondary"
            class="m-2"
          >
            No products matched your search.
          </b-alert>

          <div
            v-if="portfolioProductsData.length > portfolioProductsTable.perPage"
            class="d-flex justify-content-between px-2"
          >
            <div class="my-2">
              <span class="pb-1">Rows Per Page</span>
              <b-form-select
                v-model="portfolioProductsTable.perPage"
                :options="portfolioProductsTable.perPageOption"
                class="pagination-select"
              />
            </div>

            <div class="my-2 d-flex flex-column justify-content-end">
              <b-pagination
                v-model="portfolioProductsTable.currentPage"
                :total-rows="portfolioProductsData.length"
                :per-page="portfolioProductsTable.perPage"
                align="right"
                size="sm"
                class="my-0"
              />
            </div>
          </div>
        </b-card>

        <!-- Sub Index Data Filter -->
        <b-sidebar
          v-model="subIndexListFilterShown"
          :backdrop="true"
          :no-header="true"
          :body-class="filterSidebarClass"
          right
        >
          <div class="p-2">
            <div>
              <div class="mb-1 d-flex justify-content-between align-items-center">
                <h6 class="mb-0">
                  Available Timeseries
                </h6>
                <span
                  class="text-primary cursor-pointer"
                  @click="subIndexListFilterShown = false"
                >
                  <feather-icon
                    icon="XIcon"
                    size="20"
                  />
                </span>
              </div>
            </div>
            <b-card>
              <div
                v-for="category in subIndexList"
                :key="category.categoryKey"
                class="filter-category"
              >
                <div class="d-flex justify-content-between">
                  <h6 class="filter-title">
                    {{ category.categoryLabel }}
                  </h6>
                </div>
                <b-form-checkbox-group
                  v-model="subIndexListFilter[category.categoryKey]"
                  class="filter-checkbox-group"
                  :options="category.items"
                  text-field="label"
                  value-field="value"
                  stacked
                  @change="loadSubIndexData"
                />
              </div>
            </b-card>
          </div>
        </b-sidebar>

        <!-- Product List Modal -->
        <b-modal
          v-model="productListModal.show"
          title="Products"
          size="lg"
          scrollable
          hide-footer
        >
          <div>
            <b-input-group class="mb-2">
              <b-input-group-prepend is-text>
                <feather-icon icon="SearchIcon" />
              </b-input-group-prepend>
              <b-form-input
                v-model="productListModal.filter"
                placeholder="Search (ID or name)"
              />
            </b-input-group>
            <div
              v-if="productListModalData.length > 0"
              class="mb-1"
            >
              <span class="font-weight-bold">{{ productListModalNoData.length > 0 ? 'With Live Data' : 'Products' }}</span>
              <b-badge
                pill
                variant="primary"
                class="ml-50"
              >{{ productListModalData.length }}</b-badge>
            </div>
            <b-table
              v-if="productListModalData.length > 0"
              :items="productListModalData"
              :fields="productListModalFields"
              :per-page="productListModal.perPage"
              :current-page="productListModal.currentPage"
              responsive
              hover
              @row-clicked="onProductClicked"
            />
            <div
              v-if="productListModalData.length > 5"
              class="py-2 d-flex justify-content-between"
            >
              <div class="my-2">
                <span class="pb-1">Rows Per Page</span>
                <b-form-select
                  v-model="productListModal.perPage"
                  :options="productListModal.perPageOption"
                  class="pagination-select"
                />
              </div>

              <div class="my-2 d-flex flex-column justify-content-end">
                <b-pagination
                  v-model="productListModal.currentPage"
                  :total-rows="productListModalData.length"
                  :per-page="productListModal.perPage"
                  align="right"
                  size="sm"
                  class="my-0"
                />
              </div>
            </div>
            <div
              v-if="productListModalNoData.length > 0"
              class="mt-1 mb-1"
            >
              <span class="font-weight-bold">No Live Data</span>
              <b-badge
                pill
                variant="secondary"
                class="ml-50"
              >{{ productListModalNoData.length }}</b-badge>
            </div>
            <b-table
              v-if="productListModalNoData.length > 0"
              :items="productListModalNoData"
              :fields="productListModalFields"
              responsive
              hover
              @row-clicked="onProductClicked"
            />
          </div>
        </b-modal>

        <!-- Create Portfolio Modal -->
        <b-modal
          v-if="maxNumberOfProductsInPortfolio === 0
            || productIds.length <= maxNumberOfProductsInPortfolio"
          v-model="createPortfolioModal.show"
          centered
          :title="`Create Portfolio Using ${productIds.length} Products`"
          hide-footer
        >
          <create-portfolio-modal-content
            :portfolios="portfolios"
            :search-filter="searchFilter || cachedFilter"
            description="product"
            @portfolio-created="onPortfolioCreated"
          />
        </b-modal>
        <b-modal
          v-else
          v-model="createPortfolioModal.show"
          ok-only
          ok-variant="danger"
          ok-title="Close"
          modal-class="modal-danger"
          centered
          title="Product Per Portfolio Limit Reached"
        >
          <b-card-text>
            You can only include {{ maxNumberOfProductsInPortfolio }} products in one portfolio. Try to narrow your search
            to get under the limit.
          </b-card-text>
        </b-modal>
      </template>
    </div>
  </div>
</template>

<script>
import {
  BSidebar, BCard, BFormCheckboxGroup, BModal, BInputGroup, BInputGroupPrepend, BFormInput,
  BTable, BFormSelect, BPagination, BButton, BCardText, BBadge, BTabs, BTab, BSpinner, BImg, BAlert,
  BAvatar,
} from 'bootstrap-vue'
import { mapGetters } from 'vuex'
import CreatePortfolioModalContent from '@/views/components/CreatePortfolioModalContent.vue'
import UpgradeToPremium from '@/views/components/UpgradeToPremium.vue'
import { formatIsoDate } from '@/utils/formatters'
import PortfolioHeader from './PortfolioHeader.vue'
import PortfolioChartAnalysis from './PortfolioChartAnalysis.vue'
import ProductWeightingHelp from '../../components/ProductWeightingHelp.vue'

export default {
  components: {
    BSidebar,
    BCard,
    BFormCheckboxGroup,
    BModal,
    BInputGroup,
    BInputGroupPrepend,
    BFormInput,
    BTable,
    BFormSelect,
    BPagination,
    BButton,
    BCardText,
    BBadge,
    BTabs,
    BTab,
    BSpinner,
    BImg,
    BAlert,
    BAvatar,
    PortfolioHeader,
    PortfolioChartAnalysis,
    ProductWeightingHelp,
    CreatePortfolioModalContent,
    UpgradeToPremium,
  },
  props: {
    portfolioType: {
      type: [String, Number],
      required: false,
      default: 'u',
    },
    searchFilter: {
      type: [Object],
      required: false,
      default() {
        return null
      },
    },
  },
  data() {
    return {
      virtualDailyUsageReached: false,
      userCreatedMonthlyLimitReached: false,
      userCreatedLimitMessage: undefined,
      apiError: null,
      portfolioData: null,
      portfolioCounts: null,
      portfolioCountsRequestId: 0,
      chartDataLoading: true,
      chartDataRequestId: 0,
      loadedReportContext: null,
      portfolioName: '',
      dataUpdated: null,
      evolutionData: null,
      indexUnderlyingVolRatio: null,
      productWeightMode: 'equal',
      weightTabIndex: 0,
      allowedWeightingMode: null,
      weightTabInitialised: false,
      productWeightModeOptions: [
        { name: 'Equally weighted', value: 'equal' },
        { name: 'Notional weighted', value: 'notional' },
      ],
      subIndexList: [],
      subIndexListFilter: {},
      subIndexListFilterShown: false,
      isGeneratingPdf: false,
      baseURL: process.env.VUE_APP_BACKEND_PATH,
      createPortfolioModal: {
        show: false,
      },
      products: [],
      portfolioProductsTable: {
        perPage: 20,
        currentPage: 1,
        perPageOption: [10, 20, 50, 100, 500],
        filter: '',
        sortBy: 'productId',
        sortDesc: false,
      },
      productListModal: {
        show: false,
        products: [],
        noDataProducts: [],
        perPage: 5,
        currentPage: 1,
        perPageOption: [3, 5, 10, 25, 50, 100],
        filter: '',
      },
    }
  },
  computed: {
    ...mapGetters('auth', [
      'portfolioMaxProducts',
      'virtualPortfolioRunsLimit',
      'isPremium',
    ]),
    ...mapGetters('app', [
      'virtualPortfolioRunsUsage',
    ]),
    filterSidebarClass() {
      return this.$store.state.appConfig.layout.skin === 'dark' ? 'filter-sidebar-dark-bg custom-scrollbar' : 'filter-sidebar-light-bg custom-scrollbar'
    },
    showPortfolioProductsTable() {
      return this.portfolioType !== 'u' && !this.chartDataLoading && this.products.length > 0
        && (this.productWeightMode !== 'notional' || this.products.some(product => Number(product.notional) > 0))
    },
    canGeneratePortfolioPdf() {
      const context = this.loadedReportContext
      return !this.isGeneratingPdf && !this.chartDataLoading && !this.apiError
        && this.isModeAllowed(this.productWeightMode)
        && this.productWeightModeOptions[this.weightTabIndex]?.value === this.productWeightMode
        && this.portfolioData?.selectedmode === this.productWeightMode
        && context?.requestId === this.chartDataRequestId
        && context?.portfolioType === this.portfolioType
        && context?.productWeightMode === this.productWeightMode
    },
    portfolioProductsExcludedCount() {
      const numberRow = this.portfolioData?.state_maturity?.content?.find(item => item.number === 'Number')
      const performanceSummaryExcludedCount = Number(numberRow?.excluded)
      if (Number.isFinite(performanceSummaryExcludedCount) && performanceSummaryExcludedCount > 0) {
        return performanceSummaryExcludedCount
      }

      return this.products.filter(product => this.hasExclusionReason(product)).length
    },
    showPortfolioProductsNotionalColumn() {
      return this.portfolioType !== 'v' && this.productWeightMode !== 'equal'
    },
    portfolioProductsFields() {
      const hasExcludedProducts = this.portfolioProductsExcludedCount > 0
      let productNameWidth = '39%'
      if (hasExcludedProducts) productNameWidth = '27%'
      if (!this.showPortfolioProductsNotionalColumn) {
        productNameWidth = hasExcludedProducts ? '35%' : '47%'
      }
      const fields = [
        {
          key: 'productId', label: 'ID', thStyle: { width: '7%' }, sortable: true,
        },
        {
          key: 'productTypeName', label: 'Type', thStyle: { width: '14%' }, sortable: true,
        },
        {
          key: 'productName', label: 'Name', thStyle: { width: productNameWidth }, sortable: true,
        },
        {
          key: 'status', label: 'Status', thStyle: { width: '8%' }, sortable: true,
        },
      ]

      if (this.showPortfolioProductsNotionalColumn) {
        fields.push({
          key: 'notional', label: 'Notional', thStyle: { width: '8%' }, sortable: true,
        })
      }

      if (hasExcludedProducts) {
        fields.push({
          key: 'exclusionReason', label: 'Exclusion Reason', thStyle: { width: '12%' }, sortable: true,
        })
      }

      fields.push(
        {
          key: 'strikeDate', label: 'Strike Date', thStyle: { width: '8%' }, sortable: true,
        },
        {
          key: 'finalMarketDate', label: 'Final Date', thStyle: { width: '8%' }, sortable: true,
        },
        {
          key: 'terminationDate', label: 'Termination Date', thStyle: { width: '8%' }, sortable: true,
        },
      )

      return fields
    },
    portfolioProductsData() {
      const products = this.products.map(product => this.normalisePortfolioProduct(product))
      if (!this.portfolioProductsTable.filter) return products

      const filter = this.portfolioProductsTable.filter.toLowerCase()
      return products.filter(product => {
        const values = [
          product.productId,
          product.productName,
          product.productTypeName,
          product.status,
          product.strikeDate,
          product.finalMarketDate,
          product.terminationDate,
        ]

        if (this.showPortfolioProductsNotionalColumn) values.push(product.notional)
        if (this.portfolioProductsExcludedCount > 0) values.push(product.exclusionReason)

        return values.some(value => String(value === null || value === undefined ? '' : value).toLowerCase().includes(filter))
      })
    },
    productListModalHasExclusionReason() {
      return [
        ...this.productListModal.products,
        ...this.productListModal.noDataProducts,
      ].some(product => this.hasExclusionReason(product))
    },
    productListModalFields() {
      const fields = [
        {
          key: 'productId', label: 'ID', thStyle: { width: '5%' }, sortable: true,
        },
      ]

      if (this.productListModalHasExclusionReason) {
        fields.push({
          key: 'exclusionReason', label: 'Reason', thStyle: { width: '5%' }, sortable: true,
        })
      }

      fields.push({
        key: 'productName', label: 'Product', thStyle: { width: this.productListModalHasExclusionReason ? '80%' : '95%' }, sortable: true,
      })

      return fields
    },
    productListModalData() {
      const noDataIds = new Set(this.productListModal.noDataProducts.map(p => p.productId))
      const products = this.normaliseProductListModalProducts(this.productListModal.products.filter(p => !noDataIds.has(p.productId)))
      if (this.productListModal.filter && this.productListModal.filter.length > 0) {
        const f = this.productListModal.filter.toLowerCase()
        return products.filter(item => item.productName.toLowerCase().search(f) !== -1 || item.productId.toString().search(f) !== -1)
      }
      return products
    },
    productListModalNoData() {
      const noDataProducts = this.normaliseProductListModalProducts(this.productListModal.noDataProducts)
      if (this.productListModal.filter && this.productListModal.filter.length > 0) {
        const f = this.productListModal.filter.toLowerCase()
        return noDataProducts.filter(item => item.productName.toLowerCase().search(f) !== -1 || item.productId.toString().search(f) !== -1)
      }
      return noDataProducts
    },
    cachedFilter() {
      let cachedFilter = localStorage.getItem('searchFilter')
      cachedFilter = cachedFilter && JSON.parse(cachedFilter)
      const expiresOn = localStorage.getItem('searchFilterExpires')

      if (!cachedFilter || !expiresOn || expiresOn < Date.now()) {
        return null
      }

      return cachedFilter
    },
    portfolios() {
      return this.$store.state.app.portfolios
    },
    productIds() {
      // Check if the report is for virtual portfolio
      if (this.portfolioType !== 'v') {
        return []
      }

      return this.products.map(p => p.productId)
    },
    maxNumberOfProductsInPortfolio() {
      return this.portfolioMaxProducts
    },
    missingNotionalCount() {
      return this.products.filter(product => !(Number(product.notional) > 0)).length
    },
    hasMissingNotionalEntries() {
      return this.missingNotionalCount > 0 && this.missingNotionalCount < this.products.length
    },
    isModeAllowed() {
      return mode => {
        if (this.portfolioType === 'u' || this.portfolioType === 'v') return true
        if (!this.allowedWeightingMode) return mode === 'equal'
        if (Array.isArray(this.allowedWeightingMode)) return this.allowedWeightingMode.includes(mode)
        return this.allowedWeightingMode === mode
      }
    },
    filterKey() {
      if (this.portfolioType === 'v') return JSON.stringify(this.searchFilter || this.cachedFilter)
      return String(this.portfolioType)
    },
  },
  watch: {
    productWeightMode(val) {
      const idx = this.productWeightModeOptions.findIndex(o => o.value === val)
      if (idx !== -1 && idx !== this.weightTabIndex) this.weightTabIndex = idx
      this.loadChartData()
      this.loadPortfolioCounts()
    },
    weightTabIndex(idx) {
      const option = this.productWeightModeOptions[idx]
      if (option && option.value !== this.productWeightMode) this.productWeightMode = option.value
    },
    subIndexListFilter: {
      handler() {
        this.loadSubIndexData()
      },
      deep: true,
    },
    'portfolioProductsTable.filter': function onPortfolioProductsFilterChange() {
      this.portfolioProductsTable.currentPage = 1
    },
    'portfolioProductsTable.perPage': function onPortfolioProductsPerPageChange() {
      this.portfolioProductsTable.currentPage = 1
    },
  },
  async created() {
    await this.initialise()
  },
  async activated() {
    await this.initialise()
  },
  methods: {
    formatIsoDate,
    async initialise() {
      if (this.portfolioType === 'v') {
        if (this.virtualPortfolioRunsLimit !== 0 && this.virtualPortfolioRunsUsage >= this.virtualPortfolioRunsLimit) {
          this.virtualDailyUsageReached = true
          return
        }
        if (this.searchFilter || this.cachedFilter) {
          const runAllowed = await this.processVirtualPortfolioRun()

          if (!runAllowed) {
            return
          }
        }
      } else if (this.portfolioType !== 'u') {
        // Re-evaluate the default weighting when returning from the portfolio editor.
        this.allowedWeightingMode = null
        this.weightTabInitialised = false
        this.productWeightMode = 'equal'
        this.weightTabIndex = 0
        await this.processUserCreatedPortfolioRun()
      }

      this.loadChartData()
      this.loadPortfolioCounts()
    },
    processUserCreatedPortfolioRun() {
      return this.$http.get('lifecycle/public/report.cfc?method=logUserCreatedPortfolioRun', {
        params: {
          portfolioid: this.portfolioType,
        },
      })
        .then(response => {
          if (!response.data.status) {
            this.userCreatedMonthlyLimitReached = true
            this.userCreatedLimitMessage = response.data.message
          }
        })
    },
    processVirtualPortfolioRun() {
      return this.$http.get('lifecycle/public/report.cfc?method=processVirtualPortfolioRunRequest')
        .then(response => {
          this.$store.commit(
            'app/UPDATE_VIRTUAL_PORTFOLIO_RUNS_USAGE',
            response.data.viewedCount,
          )

          if (!response.data.status) {
            this.virtualDailyUsageReached = true
            return false
          }

          return true
        })
        .catch(() => {
          console.error('FIX ME')
          return false
        })
    },
    parseVolatilityValue(value) {
      if (!value) return null
      // Remove % symbol if present and convert to number
      const cleanValue = typeof value === 'string' ? value.replace('%', '') : value
      const numValue = parseFloat(cleanValue)
      return Number.isNaN(numValue) ? null : numValue
    },
    loadSubIndexData() {
      if (this.portfolioType !== 'u') return

      this.$http.get('lifecycle/public/data.cfc?method=getSubIndexData', {
        params: {
          productweightingmode: this.productWeightMode,
          indexlistdata: this.subIndexListFilter,
        },
      })
        .then(response => {
          // Create a new object reference to ensure Vue detects the change
          this.evolutionData = { ...response.data }
          this.indexUnderlyingVolRatio = this.parseVolatilityValue(response.data?.SHOWDATA?.INDEXUNDERLYINGVOLRATIO)
        })
        .catch(err => {
          console.error('Error loading sub-index data:', err)
        })
    },
    loadPortfolioCounts() {
      this.portfolioCountsRequestId += 1
      const requestId = this.portfolioCountsRequestId
      this.portfolioCounts = null
      if (this.portfolioType === 'u') return

      let endpoint
      const params = {}

      if (this.portfolioType === 'v') {
        endpoint = 'lifecycle/public/report.cfc?method=getVirtualPortfolioCounts'
        const filterToUse = this.searchFilter || this.cachedFilter
        if (!filterToUse) return
        params.searchfilter = JSON.stringify(filterToUse)
      } else {
        endpoint = 'lifecycle/public/report.cfc?method=getUserPortfolioCounts'
        params.portfolioid = this.portfolioType
        params.productweightingmode = this.productWeightMode
      }

      this.$http.get(endpoint, { params })
        .then(response => {
          if (requestId !== this.portfolioCountsRequestId) return
          this.portfolioCounts = response.data
        })
        .catch(err => {
          if (requestId !== this.portfolioCountsRequestId) return
          console.error('Error loading portfolio counts:', err)
        })
    },
    loadChartData() {
      this.apiError = null
      this.chartDataRequestId += 1
      const requestId = this.chartDataRequestId
      const { portfolioType, productWeightMode } = this
      const isCurrentRequest = () => this.chartDataRequestId === requestId
        && this.portfolioType === portfolioType && this.productWeightMode === productWeightMode
      const finishLoading = () => {
        if (isCurrentRequest()) {
          this.chartDataLoading = false
        }
      }
      this.chartDataLoading = true
      this.loadedReportContext = null
      this.portfolioData = null
      this.evolutionData = null
      this.indexUnderlyingVolRatio = null
      // Determine which API endpoint to use based on portfolio type
      let reportEndpoint
      const reportParams = { productweightingmode: this.productWeightMode }

      if (this.portfolioType === 'u') {
        reportEndpoint = 'lifecycle/public/report.cfc?method=getUniversePortfolioReport'
      } else if (this.portfolioType === 'v') {
        reportEndpoint = 'lifecycle/public/report.cfc?method=getVirtualPortfolioReport'
        // Virtual portfolios need searchFilter
        // Check if searchFilter or cachedFilter exists
        if (!this.searchFilter && !this.cachedFilter) {
          finishLoading()
          return
        }

        // Cache the searchFilter if it exists
        if (this.searchFilter) {
          localStorage.setItem('searchFilter', JSON.stringify(this.searchFilter))
          localStorage.setItem('searchFilterExpires', Date.now() + 600000) // Cache expires after 10 minutes
        }

        // Use searchFilter if available, otherwise use cachedFilter
        reportParams.searchfilter = JSON.stringify(this.searchFilter || this.cachedFilter)
      } else {
        if (!this.isModeAllowed(this.productWeightMode)) {
          finishLoading()
          return
        }
        reportEndpoint = 'lifecycle/public/report.cfc?method=getUserPortfolioReport'
        reportParams.portfolioid = this.portfolioType
      }

      // Make initial API calls in parallel
      const apiCalls = [
        this.$http.get(reportEndpoint, { params: reportParams }),
      ]

      // Only load sub-index data for universe portfolios
      if (this.portfolioType === 'u') {
        apiCalls.push(this.$http.get('lifecycle/public/data.cfc?method=getSubIndexList'))
      }

      Promise.all(apiCalls)
        .then(responses => {
          if (!isCurrentRequest()) return null
          const reportResponse = responses[0]
          if (!reportResponse.data || !reportResponse.data.portfoliosummary) {
            throw new Error(reportResponse.data?.message || 'Failed to load portfolio data')
          }
          // Store portfolio data for child components
          this.portfolioData = reportResponse.data.portfoliosummary
          // Store allowed weighting modes
          this.allowedWeightingMode = reportResponse.data.portfoliosummary.allowedmode || null
          // Default to notional tab if allowed, otherwise equal weighted (only on first load)
          if (!this.weightTabInitialised) {
            this.weightTabIndex = (this.portfolioType !== 'v' && this.isModeAllowed('notional')) ? 1 : 0
            this.weightTabInitialised = true
          }
          // Store portfolio name
          this.portfolioName = reportResponse.data.portfoliosummary.portfolioname || ''
          // Store products array for modal
          this.products = reportResponse.data.portfoliosummary.products || []
          // Store last updated date for universe overview
          this.dataUpdated = reportResponse.data.portfoliosummary.latestrundate || null

          // Only process sub-index data for universe portfolios
          if (this.portfolioType === 'u' && responses[1]) {
            const subIndexListResponse = responses[1]
            this.subIndexList = subIndexListResponse.data
            const subIndexListFilter = {}

            // Initialize all category keys first (important for Vue reactivity)
            this.subIndexList.forEach(category => {
              subIndexListFilter[category.categoryKey] = []
            })

            // Build filter object with pre-selected indices
            this.subIndexList.forEach(category => {
              category.items.forEach(item => {
                if (item.isChecked) {
                  subIndexListFilter[category.categoryKey].push(item.value)
                }
              })
            })

            this.subIndexListFilter = subIndexListFilter

            // Load chart data with selected indices
            return this.$http.get('lifecycle/public/data.cfc?method=getSubIndexData', {
              params: {
                productweightingmode: this.productWeightMode,
                indexlistdata: this.subIndexListFilter,
              },
            })
          }
          return null
        })
        .then(response => {
          if (!isCurrentRequest()) return
          if (response && this.portfolioType === 'u') {
            // For universe portfolios, use sub-index data
            this.evolutionData = response.data
            this.indexUnderlyingVolRatio = this.parseVolatilityValue(response.data?.SHOWDATA?.INDEXUNDERLYINGVOLRATIO)
          }
          // For non-universe portfolios, evolution data comes from indexchart
          // (portfoliochart contains different data: pre-strike, live, matured)
          if (this.portfolioType !== 'u' && this.portfolioData?.indexchart) {
            this.evolutionData = this.portfolioData.indexchart
            this.indexUnderlyingVolRatio = this.parseVolatilityValue(this.portfolioData.indexchart?.SHOWDATA?.INDEXUNDERLYINGVOLRATIO)
          }
          this.loadedReportContext = { requestId, portfolioType, productWeightMode }
        })
        .catch(err => {
          if (!isCurrentRequest()) return
          this.apiError = err?.response?.data?.message || err?.message || 'Unable to load portfolio data. Please try again later.'
        })
        .finally(finishLoading)
    },
    editPortfolio() {
      this.$router.push({ name: 'portfolio-edit', params: { portfolioId: this.portfolioType } })
    },
    async generatePortfolioPdf() {
      if (!this.canGeneratePortfolioPdf) return
      const context = this.loadedReportContext
      // Flush child prop/watch updates before taking the export snapshot.
      await this.$nextTick()
      if (!this.canGeneratePortfolioPdf || this.loadedReportContext !== context) return
      const analysis = this.$refs.chartAnalysis
      if (!analysis || analysis.portfolioData !== this.portfolioData) return

      this.isGeneratingPdf = true
      let form
      try {
        const charts = analysis.collectCharts(context.productWeightMode)
        const payload = JSON.stringify({
          charts,
          portfolioName: this.portfolioName,
          productWeightMode: context.productWeightMode,
          portfolioReturnsMode: analysis.getReturnsMode(),
          missingNotionalCount: this.missingNotionalCount,
          calculationStatus: this.portfolioData.calculationstatus,
          portfolioPerformanceSummary: this.portfolioData.state_maturity,
        })

        form = document.createElement('form')
        form.method = 'POST'
        form.action = `${this.baseURL}cfc/dynamicreport/portfolio/portfoliopdf.cfm?portfolioId=${context.portfolioType}`
        form.target = `portfolio-pdf-${context.portfolioType}`

        const input = document.createElement('input')
        input.type = 'hidden'
        input.name = 'payload'
        input.value = payload

        form.appendChild(input)
        document.body.appendChild(form)
        window.open('', form.target)
        form.submit()
        this.addPortfolioDownloadHit('portfolio_download_pdf')
      } catch (err) {
        this.$bvToast.toast('Unable to prepare the PDF charts. Please try again.', {
          title: 'PDF export',
          variant: 'danger',
        })
      } finally {
        if (form?.parentNode) form.parentNode.removeChild(form)
        this.isGeneratingPdf = false
      }
    },
    addPortfolioCsvDownloadHit() {
      this.addPortfolioDownloadHit('portfolio_download_csv')
    },
    addPortfolioDownloadHit(hitname) {
      if (this.portfolioType === 'u' || this.portfolioType === 'v') return

      this.$http
        .get('hits.cfc', {
          params: {
            method: 'addPortfolioHit',
            hitname,
            portfolioid: this.portfolioType,
          },
        })
    },
    normalisePortfolioProduct(product) {
      const exclusionReason = product.exclusionReason ?? product.exclusionreason

      return {
        ...product,
        productTypeName: product.productTypeName || product.producttypename || '',
        ptIconName: product.ptIconName || product.pticonname || '',
        ptIconColor: product.ptIconColor || product.pticoncolor || '',
        providerId: product.providerId || product.providerid || '',
        strikeDate: product.strikeDate || product.date_1_strike || '',
        finalMarketDate: product.finalMarketDate || product.date_2_finalmarket || '',
        terminationDate: product.terminationDate || product.terminationdate || '',
        exclusionReason: this.hasExclusionReason(product) ? String(exclusionReason).trim() : 'None',
      }
    },
    statusVariant(status) {
      switch (status) {
        case 'Current':
          return 'light-warning'
        case 'Live':
          return 'light-success'
        case 'Matured':
          return 'light-secondary'
        default:
          return 'light-secondary'
      }
    },
    formatNotional(value) {
      if (value === null || value === undefined || value === '') return '-'

      const parsed = Number(value)
      if (!Number.isFinite(parsed)) return String(value)

      return parsed.toLocaleString()
    },
    onPortfolioProductClicked(item, index, event) {
      if (event.ctrlKey || event.button === 1) {
        const route = this.$router.resolve({ name: 'product-report', params: { reportNumber: item.productId } })
        window.open(route.href, '_blank')
      } else {
        this.$router.push({ name: 'product-report', params: { reportNumber: item.productId } })
      }
    },
    showProductsListModal({ ids, noDataIds }) {
      this.productListModal.filter = ''
      this.productListModal.products = this.products.filter(pd => ids.includes(pd.productId))
      this.productListModal.noDataProducts = this.products.filter(pd => noDataIds.includes(pd.productId))
      this.productListModal.currentPage = 1
      this.productListModal.perPage = 5
      this.productListModal.show = true
    },
    hasExclusionReason(product) {
      const exclusionReason = product?.exclusionReason ?? product?.exclusionreason
      if (exclusionReason === undefined || exclusionReason === null) return false

      const normalisedExclusionReason = String(exclusionReason).trim()
      return normalisedExclusionReason !== '' && normalisedExclusionReason.toLowerCase() !== 'none'
    },
    normaliseProductListModalProducts(products) {
      if (!this.productListModalHasExclusionReason) return products

      return products.map(product => {
        if (this.hasExclusionReason(product)) return product

        return {
          ...product,
          exclusionReason: 'None',
        }
      })
    },
    onProductClicked(item, index, event) {
      // Close the modal
      this.productListModal.show = false

      if (event.ctrlKey || event.button === 1) {
        const route = this.$router.resolve({ name: 'product-report', params: { reportNumber: item.productId } })
        window.open(route.href, '_blank')
      } else {
        this.$router.push({ name: 'product-report', params: { reportNumber: item.productId } })
      }
    },
    onPortfolioCreated(portfolio) {
      // Show success toast
      this.$bvToast.toast('Portfolio Created', {
        title: 'Success',
        variant: 'success',
        solid: true,
      })

      // Hide the modal
      this.createPortfolioModal.show = false

      // Update store value
      this.$store.dispatch('app/updatePortfolios')
      this.$store.commit('app/SET_PORTFOLIO_UPDATED', true)

      // Redirect to newly created portfolio page
      this.$router.push({ name: 'portfolio-report', params: { portfolioType: portfolio.portfolioId } })
    },
  },
}
</script>

<style lang="scss" scoped>
.filter-checkbox-group::v-deep > .custom-control {
  margin-bottom: 0.75rem;
}

.filter-category {
  margin-top: 2rem;

  &:first-child {
    margin-top: 0 !important;
  }
}

.filter-title {
  margin-bottom: 1rem;
}

.cursor-pointer {
  cursor: pointer;
}

.pagination-select {
  margin-left: 0.5rem;
  width: auto;
}

.portfolio-product-logo {
  height: 32px;
  object-fit: contain;
  width: 32px;
}

.portfolio-products-search {
  max-width: 360px;
  width: 100%;
}
</style>
